import {
  formatEventDate,
  mapPageEventToDatalayer,
} from 'services/datalayer/utils';
import {
  CartFragment,
  CartItemFragment,
  CouponCodeFragment,
  MoneyInfoFragment,
  OrderFragment,
  TicketFragment,
} from 'types/api/tickets.types';

const calculateDiscount = (
  originalPrice: MoneyInfoFragment | undefined,
  discountedPrice: MoneyInfoFragment | undefined,
) => {
  if (!originalPrice || !discountedPrice) return 0;
  if (originalPrice.amount === discountedPrice.amount) return 0;

  return originalPrice.amount - discountedPrice.amount;
};

export const mapTicketToDataLayer = (
  item: Pick<TicketFragment, 'id' | 'name'>,
  event: ReturnType<typeof mapPageEventToDatalayer>,
  {
    originalPrice,
    discountedPrice,
  }: {
    originalPrice: MoneyInfoFragment | undefined;
    discountedPrice?: MoneyInfoFragment | undefined;
  },
) => ({
  itemId: item.id,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  index: 0,
  discount: calculateDiscount(originalPrice, discountedPrice),
  price: discountedPrice?.amount ?? 0,
  quantity: 1,
});

const mapCartItemToDataLayer = (
  item: CartItemFragment,
  idx: number = 0,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  itemId: item.catalogueItemId,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  discount: calculateDiscount(item.originalItemPrice, item.discountedItemPrice),
  index: idx,
  price: item.discountedItemPrice.amount,
  quantity: item.quantity,
});

export const mapOrderToDatalayer = (
  order: OrderFragment,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: order.totalPrice.amount,
  items:
    order.items?.map((item, idx) => mapCartItemToDataLayer(item, idx, event)) ??
    [],
  transactionId: order.id,
  coupon: findCouponCode(order.couponCodes),
  taxAmount: order.vatAmount.amount,
  // Always 0 for now
  shippingAmount: 0,
  paymentType:
    order.totalPrice.amount === 0 ? 'FreeOfCharge' : order.paymentMethod,
  shippingTier: 'Online',
});

export const mapCartToDatalayer = (
  cart: CartFragment,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: cart.totalPrice.amount,
  items:
    cart.items?.map((item, idx) => mapCartItemToDataLayer(item, idx, event)) ??
    [],
});

const findCoupon = (coupons: Array<CouponCodeFragment>) =>
  coupons.find((coupon) => coupon.couponType === 'RegularCoupon');

export const findValidCouponCode = (coupons: Array<CouponCodeFragment>) => {
  const coupon = findCoupon(coupons);
  return (coupon?.discountValue?.amount ?? 0 > 0) ? coupon?.code : undefined;
};

const findCouponCode = (coupons: Array<CouponCodeFragment>) =>
  findCoupon(coupons)?.code;
